/**
 * src/lib/audio.ts
 * ------------------
 * Audio utilities for the Voice-to-Voice pipeline.
 *
 * PCMPlayer — streams PCM16 chunks from OpenAI Realtime API to the speaker.
 *   feed(pcm16)  — schedule a raw PCM16 ArrayBuffer for gapless playback
 *   finish()     — promise that resolves when the last queued chunk ends
 *   destroy()    — stop all audio immediately (barge-in / session end)
 *
 * startRecording(onChunk, onAmplitude?) — capture microphone as PCM16 @ 24 kHz
 *   onChunk(pcm16: ArrayBuffer) — called ~every 170 ms with a raw PCM16 frame
 *   onAmplitude(number[])       — called at ~30 fps with normalized freq bins
 *   returns RecordingHandle { stop(), stream }
 */

const SAMPLE_RATE = 24000

// ─── PCMPlayer ────────────────────────────────────────────────────────────────

export class PCMPlayer {
  private ctx: AudioContext
  private nextStartTime = 0
  private sources: AudioBufferSourceNode[] = []
  private finishResolve: (() => void) | null = null

  constructor() {
    this.ctx = new AudioContext({ sampleRate: SAMPLE_RATE })
    this.nextStartTime = this.ctx.currentTime
  }

  feed(pcm16: ArrayBuffer): void {
    const samples = new Int16Array(pcm16)
    const float32 = new Float32Array(samples.length)
    for (let i = 0; i < samples.length; i++) {
      float32[i] = samples[i] / 32768
    }

    const buf = this.ctx.createBuffer(1, float32.length, SAMPLE_RATE)
    buf.copyToChannel(float32, 0)

    const source = this.ctx.createBufferSource()
    source.buffer = buf
    source.connect(this.ctx.destination)
    this.sources.push(source)

    const now = this.ctx.currentTime
    const startAt = Math.max(now, this.nextStartTime)
    source.start(startAt)
    this.nextStartTime = startAt + buf.duration

    source.onended = () => {
      this.sources = this.sources.filter((s) => s !== source)
      if (this.sources.length === 0 && this.finishResolve) {
        this.finishResolve()
        this.finishResolve = null
      }
    }
  }

  finish(): Promise<void> {
    if (this.sources.length === 0) return Promise.resolve()
    return new Promise((resolve) => {
      this.finishResolve = resolve
    })
  }

  destroy(): void {
    if (this.finishResolve) {
      this.finishResolve()
      this.finishResolve = null
    }
    this.sources.forEach((s) => { try { s.stop() } catch { /* already stopped */ } })
    this.sources = []
    this.nextStartTime = 0
    this.ctx.close().catch(() => {})
  }
}

// ─── Recording ────────────────────────────────────────────────────────────────

export interface RecordingHandle {
  stop: () => void
  stream: MediaStream
}

export async function startRecording(
  onChunk: (pcm16: ArrayBuffer) => void,
  onAmplitude?: (amplitudes: number[]) => void,
): Promise<RecordingHandle> {
  // echoCancellation stops the assistant's own audio (played through speakers)
  // from being picked up by the mic and re-triggering the voice-activity detector,
  // which would make the agent talk over / respond to itself.
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    video: false,
  })
  const ctx = new AudioContext({ sampleRate: SAMPLE_RATE })
  const source = ctx.createMediaStreamSource(stream)

  const analyser = ctx.createAnalyser()
  analyser.fftSize = 256
  source.connect(analyser)

  let rafId: number
  if (onAmplitude) {
    const data = new Uint8Array(analyser.frequencyBinCount)
    const tick = () => {
      analyser.getByteFrequencyData(data)
      onAmplitude(Array.from(data.slice(0, 40)).map((v) => v / 255))
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
  }

  // ScriptProcessorNode captures float32 frames; we convert to PCM16 for the backend
  const bufferSize = 4096
  const processor = ctx.createScriptProcessor(bufferSize, 1, 1)
  source.connect(processor)
  processor.connect(ctx.destination)

  processor.onaudioprocess = (e) => {
    const float32 = e.inputBuffer.getChannelData(0)
    const int16 = new Int16Array(float32.length)
    for (let i = 0; i < float32.length; i++) {
      const s = Math.max(-1, Math.min(1, float32[i]))
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
    }
    onChunk(int16.buffer.slice(0))
  }

  const stop = () => {
    cancelAnimationFrame(rafId)
    processor.disconnect()
    source.disconnect()
    ctx.close().catch(() => {})
    stream.getTracks().forEach((t) => t.stop())
  }

  return { stop, stream }
}
